import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import RestoreIcon from '@mui/icons-material/Restore';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import ProductForm from './ProductForm';
import ProductOrderGuideForm from './ProductOrderGuideForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridIdSearch from '../../reuseable-components/AGGrid/AGGridIdSearch';
import ExcelPriceUpdate from './ExcelPriceUpdate';
import ExcelProductUpload from './ExcelProductUpload';
import HistoryReport from './HistoryReport';

export default function ProductList() {
  const gridRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uomTypes, setUOMTypes] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [openProductForm, setOpenProductForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openOrderGuideForm, setOpenOrderGuideForm] = useState(false);
  const [openExcelVendorPriceUpdate, setOpenExcelVendorPriceUpdate] = useState(false);
  const [openExcelCustomerPriceUpdate, setOpenExcelCustomerPriceUpdate] = useState(false);
  const [openExcelProductUpload, setOpenExcelProductsUpload] = useState(false);
  const [openExcelPriceMenu, setOpenExcelPriceMenu] = useState(null);
  const [gridIdSearch] = useState(AGGridIdSearch((data) => { setEditProduct(data); setOpenProductForm(true) }));
  const [openHistory, setOpenHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/product/products")
      .then(res => {
        setProducts(res.data.products.map(product => ({
          ...product,
          vendor: res.data.vendors.find(vendor => vendor.value === product.vendorId)?.label,
          category: res.data.categories.find(category => category.value === product.productCategoryId)?.label
        })));
        setVendors(res.data.vendors);
        setCategories(res.data.categories);
        setUOMTypes(res.data.uomTypes.map(type => ({ label: type, value: type })));
      })
      .catch(() => enqueueSnackbar('Product list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const deleteProduct = (id) => {
    axiosInstance.post(`/product/deleteProduct?id=${id}`)
      .then(() => {
        setProducts(products => [...products.filter(product => product.id !== id)]);
        enqueueSnackbar('Product deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Product delete failed', { variant: 'error' }))
  };

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'NHI Item Id',
      filter: true,
      width: 150,
    },
    {
      field: 'oldId',
      headerName: 'Old NHI Id',
      filter: true,
      minWidth: 150,
    },
    {
      field: 'vendorItemId',
      headerName: 'SKU',
      filter: true,
      width: 150,
    },
    {
      field: 'vendor',
      filter: true,
      width: 150,
    },
    {
      field: 'name',
      headerName: 'Product Name',
      filter: true,
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'category',
      filter: true,
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      filter: false,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <Tooltip title={"Configure order guide items"} arrow placement="top">
          <IconButton edge="end" color="secondary" onClick={() => { setEditProduct(params.data); setOpenOrderGuideForm(true); }} disableRipple sx={{ px: 0 }}>
            <BuildIcon />
          </IconButton>
        </Tooltip>
        <EditIconButton onClick={() => { setEditProduct(params.data); setOpenProductForm(true); }} />
        <Tooltip title="History Report" arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0 }}
            onClick={() => {
              setHistoryData(params.data);
              setOpenHistory(true);  }}>
            <RestoreIcon />
          </IconButton>
        </Tooltip>

        <DeleteIconButton content={`Are you sure you want to delete ${params.data.name}?`} action={() => deleteProduct(params.data.id)} />
      </Stack>
    }
  ], []);

  return (<>
    <Helmet>
      <title>Product List | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setEditProduct(null); setOpenProductForm(true); }} color="primary" size="small">
          <Typography>Add Product</Typography>
        </Button>
        <Button variant="contained" onClick={() => { setOpenExcelProductsUpload(true) }} color="primary" size="small">
          <Typography>Bulk Upload Products</Typography>
        </Button>
        <Button variant="contained" onClick={(e) => setOpenExcelPriceMenu(e.currentTarget)} color="primary" size="small">
          <Typography>Bulk Update Prices</Typography>
        </Button>
        {openExcelPriceMenu && <Menu anchorEl={openExcelPriceMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={Boolean(openExcelPriceMenu)}
          onClose={() => setOpenExcelPriceMenu(null)}
        >
          <MenuItem onClick={() => { setOpenExcelVendorPriceUpdate(true); setOpenExcelPriceMenu(null); }}>
            <Typography>Update Vendor Prices</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setOpenExcelCustomerPriceUpdate(true); setOpenExcelPriceMenu(null); }}>
            <Typography>Update Customer Prices</Typography>
          </MenuItem>
        </Menu>}
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} {...gridIdSearch} columnDefs={columns} rowData={products} loading={loading} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <ProductForm editProduct={editProduct} open={openProductForm} close={() => { setEditProduct(null); setOpenProductForm(false); }}
        setProducts={setProducts} vendors={vendors} categories={categories} uomTypes={uomTypes} />
      <HistoryReport
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        data={historyData}
      />
      <ProductOrderGuideForm editProduct={editProduct} open={openOrderGuideForm} close={() => { setEditProduct(null); setOpenOrderGuideForm(false); }} />
      <ExcelPriceUpdate open={openExcelVendorPriceUpdate} setOpen={setOpenExcelVendorPriceUpdate} />
      <ExcelPriceUpdate open={openExcelCustomerPriceUpdate} setOpen={setOpenExcelCustomerPriceUpdate} isCustomerUpload />
      <ExcelProductUpload open={openExcelProductUpload} setOpen={setOpenExcelProductsUpload} />
    </Box>
  </>);
}
